import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, CheckCircle2, AlertTriangle, ArrowRight, Loader2, ShieldCheck, RotateCcw, UploadCloud, FileText } from 'lucide-react';
import { reviewResume, MissingKeyError, type ResumeReview } from '../lib/resumeReview';
import { extractTextFromFile, ACCEPT_ATTR, UnsupportedFileError } from '../lib/extractText';
interface ResumeReviewModalProps {
  open: boolean;
  onClose: () => void;
}
const GOALS = ['Board Appointment', 'Advisory Board Seat', 'Chairman / Board Chair', 'CEO / President', 'C-Suite Transition', 'Executive Promotion'];
export function ResumeReviewModal({
  open,
  onClose
}: ResumeReviewModalProps) {
  const [resumeText, setResumeText] = useState('');
  const [goal, setGoal] = useState(GOALS[0]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<ResumeReview | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFile = async (file: File) => {
    setParsing(true);
    setError(null);
    setStatus('idle');
    try {
      const text = await extractTextFromFile(file);
      if (!text || text.trim().length < 20) {
        setError(`We couldn't read enough text from "${file.name}". If it's a scanned or image-based PDF, please paste the text instead.`);
        setStatus('error');
        setFileName(null);
      } else {
        setResumeText(text);
        setFileName(file.name);
      }
    } catch (err) {
      setError(err instanceof UnsupportedFileError ? err.message : err instanceof Error ? err.message : 'We could not read that file. Please try another document or paste the text.');
      setStatus('error');
      setFileName(null);
    } finally {
      setParsing(false);
    }
  };
  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so re-selecting the same file still fires onChange.
    e.target.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };
  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resumeText.trim().length < 60) {
      setError('Please paste at least a few lines of your resume or career summary so we can provide a meaningful review.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      const result = await reviewResume(resumeText, goal);
      setReview(result);
      setStatus('done');
    } catch (err) {
      if (err instanceof MissingKeyError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
      setStatus('error');
    }
  };
  const reset = () => {
    setStatus('idle');
    setReview(null);
    setError(null);
    setFileName(null);
    setResumeText('');
  };
  return <AnimatePresence data-id="element-280">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} aria-modal="true" role="dialog" aria-labelledby="resume-review-title" data-id="element-281">
          <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={onClose} data-id="element-282" />

          <motion.div className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-sm shadow-2xl" initial={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-283">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between bg-navy-900 px-6 sm:px-8 py-5" data-id="element-284">
              <div className="flex items-center gap-3" data-id="element-285">
                <div className="w-9 h-9 rounded-sm bg-gold/15 flex items-center justify-center" data-id="element-286">
                  <Sparkles className="w-5 h-5 text-gold" data-id="element-287" />
                </div>
                <div data-id="element-288">
                  <h2 id="resume-review-title" className="text-white font-serif-display text-xl font-bold leading-tight" data-id="element-289">
                    AI Resume Review
                  </h2>
                  <p className="text-gray-400 text-xs" data-id="element-290">
                    Board & executive readiness analysis
                  </p>
                </div>
              </div>
              <button onClick={onClose} aria-label="Close resume review" className="text-gray-400 hover:text-white transition-colors" data-id="element-291">
                <X className="w-6 h-6" data-id="element-292" />
              </button>
            </div>

            <div className="px-6 sm:px-8 py-7" data-id="element-293">
              {status !== 'done' ? <form onSubmit={handleSubmit} className="space-y-6" data-id="element-294">
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-295">
                    Upload your resume document or paste your executive bio /
                    career summary below. Our AI strategist will assess your
                    board and executive readiness and return targeted,
                    governance-focused feedback. Your document is processed in
                    your browser and never stored.
                  </p>

                  <div data-id="element-296">
                    <label htmlFor="goal" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-297">
                      Target Goal
                    </label>
                    <select id="goal" value={goal} onChange={e => setGoal(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-sm bg-white focus:ring-2 focus:ring-gold focus:border-gold outline-none transition-all" data-id="element-298">
                      {GOALS.map(g => <option key={g} data-id="element-299">{g}</option>)}
                    </select>
                  </div>

                  {/* Document upload */}
                  <div data-id="element-300">
                    <span className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-301">
                      Upload Resume Document
                    </span>
                    <input ref={fileInputRef} type="file" accept={ACCEPT_ATTR} onChange={onFileInputChange} className="sr-only" aria-label="Upload resume document" data-id="element-302" />
                    {fileName ? <div className="flex items-center justify-between gap-3 border border-navy-200 bg-navy-50 rounded-sm px-4 py-3" data-id="element-303">
                        <div className="flex items-center gap-3 min-w-0" data-id="element-304">
                          <FileText className="w-5 h-5 text-gold flex-shrink-0" data-id="element-305" />
                          <span className="text-sm text-navy-900 font-medium truncate" data-id="element-306">
                            {fileName}
                          </span>
                          <span className="text-xs text-green-600 flex-shrink-0" data-id="element-307">
                            ✓ Extracted
                          </span>
                        </div>
                        <button type="button" onClick={() => {
                  setFileName(null);
                  setResumeText('');
                }} className="text-gray-400 hover:text-navy-900 transition-colors flex-shrink-0" aria-label="Remove uploaded file" data-id="element-308">
                          <X className="w-5 h-5" data-id="element-309" />
                        </button>
                      </div> : <button type="button" onClick={() => fileInputRef.current?.click()} onDragOver={e => {
                e.preventDefault();
                setDragOver(true);
              }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} className={`w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-sm px-4 py-8 transition-colors ${dragOver ? 'border-gold bg-gold-50' : 'border-gray-300 hover:border-gold hover:bg-navy-50/50'}`} data-id="element-310">
                        {parsing ? <>
                            <Loader2 className="w-6 h-6 text-gold animate-spin" data-id="element-311" />
                            <span className="text-sm text-navy-700" data-id="element-312">
                              Reading document…
                            </span>
                          </> : <>
                            <UploadCloud className="w-7 h-7 text-navy-400" data-id="element-313" />
                            <span className="text-sm text-navy-900 font-medium" data-id="element-314">
                              Drop your file here or{' '}
                              <span className="text-gold-600 underline" data-id="element-315">
                                browse
                              </span>
                            </span>
                            <span className="text-xs text-gray-400" data-id="element-316">
                              PDF, Word (.docx), or text — up to 10 MB
                            </span>
                          </>}
                      </button>}
                  </div>

                  <div className="relative flex items-center" data-id="element-317">
                    <div className="flex-grow border-t border-gray-200" data-id="element-318" />
                    <span className="mx-3 text-xs uppercase tracking-wider text-gray-400" data-id="element-319">
                      or paste text
                    </span>
                    <div className="flex-grow border-t border-gray-200" data-id="element-320" />
                  </div>

                  <div data-id="element-321">
                    <label htmlFor="resume" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-322">
                      Resume / Career Summary
                    </label>
                    <textarea id="resume" value={resumeText} onChange={e => {
                setResumeText(e.target.value);
                if (fileName) setFileName(null);
              }} rows={10} placeholder="Paste your resume content here, or upload a document above..." className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none transition-all resize-y text-sm leading-relaxed" data-id="element-323" />
                    <p className="mt-1.5 text-xs text-gray-400" data-id="element-324">
                      {resumeText.trim().length} characters
                    </p>
                  </div>

                  {status === 'error' && error && <div className="flex items-start gap-3 bg-red-50 border border-red-100 text-red-800 rounded-sm px-4 py-3 text-sm" data-id="element-325">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-326" />
                      <span data-id="element-327">{error}</span>
                    </div>}

                  <button type="submit" disabled={status === 'loading'} className="w-full bg-gold hover:bg-gold-600 text-navy-900 px-6 py-4 rounded-sm font-semibold text-lg transition-all duration-300 flex items-center justify-center group disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-328">
                    {status === 'loading' ? <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-329" />
                        Analyzing your profile...
                      </> : <>
                        Run AI Review
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-330" />
                      </>}
                  </button>
                  <p className="text-xs text-gray-400 text-center" data-id="element-331">
                    AI-generated guidance. For a fully tailored, expert-written
                    result, book a consultation with our strategists.
                  </p>
                </form> : review && <ReviewResults review={review} onReset={reset} onClose={onClose} data-id="element-332" />}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function ReviewResults({
  review,
  onReset,
  onClose
}: {
  review: ResumeReview;
  onReset: () => void;
  onClose: () => void;
}) {
  const scoreColor = review.overallScore >= 75 ? 'text-green-600' : review.overallScore >= 50 ? 'text-gold-600' : 'text-red-600';
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-7" data-id="element-333">
      {/* Score + summary */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 bg-navy-50 rounded-sm p-5" data-id="element-334">
        <div className="text-center flex-shrink-0" data-id="element-335">
          <div className={`text-5xl font-serif-display font-bold ${scoreColor}`} data-id="element-336">
            {review.overallScore}
          </div>
          <div className="text-xs uppercase tracking-wider text-navy-500 mt-1" data-id="element-337">
            Readiness Score
          </div>
        </div>
        <p className="text-navy-800 text-sm leading-relaxed" data-id="element-338">
          {review.summary}
        </p>
      </div>

      {review.recommendedHeadline && <div className="border-l-2 border-gold pl-4" data-id="element-339">
          <div className="text-xs uppercase tracking-wider text-gold-600 font-semibold mb-1" data-id="element-340">
            Suggested Executive Headline
          </div>
          <p className="text-navy-900 font-serif-display text-lg italic" data-id="element-341">
            “{review.recommendedHeadline}”
          </p>
        </div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-342">
        <div data-id="element-343">
          <h3 className="flex items-center gap-2 text-navy-900 font-bold mb-3" data-id="element-344">
            <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-345" /> Strengths
          </h3>
          <ul className="space-y-2" data-id="element-346">
            {review.strengths.map((s, i) => <li key={i} className="text-sm text-gray-700 leading-relaxed flex gap-2" data-id="element-347">
                <span className="text-green-600 mt-1" data-id="element-348">•</span>
                <span data-id="element-349">{s}</span>
              </li>)}
          </ul>
        </div>
        <div data-id="element-350">
          <h3 className="flex items-center gap-2 text-navy-900 font-bold mb-3" data-id="element-351">
            <AlertTriangle className="w-5 h-5 text-gold-600" data-id="element-352" /> Opportunities
          </h3>
          <ul className="space-y-2" data-id="element-353">
            {review.gaps.map((g, i) => <li key={i} className="text-sm text-gray-700 leading-relaxed flex gap-2" data-id="element-354">
                <span className="text-gold-600 mt-1" data-id="element-355">•</span>
                <span data-id="element-356">{g}</span>
              </li>)}
          </ul>
        </div>
      </div>

      {review.governancePositioning && <div className="bg-navy-900 text-gray-200 rounded-sm p-5" data-id="element-357">
          <h3 className="flex items-center gap-2 text-white font-bold mb-2" data-id="element-358">
            <ShieldCheck className="w-5 h-5 text-gold" data-id="element-359" /> Governance Positioning
          </h3>
          <p className="text-sm leading-relaxed text-gray-300" data-id="element-360">
            {review.governancePositioning}
          </p>
        </div>}

      {review.rewriteSuggestions.length > 0 && <div data-id="element-361">
          <h3 className="text-navy-900 font-bold mb-3" data-id="element-362">Rewrite Examples</h3>
          <div className="space-y-4" data-id="element-363">
            {review.rewriteSuggestions.map((r, i) => <div key={i} className="border border-gray-200 rounded-sm overflow-hidden" data-id="element-364">
                <div className="px-4 py-3 bg-red-50 text-sm text-red-900" data-id="element-365">
                  <span className="text-xs font-semibold uppercase tracking-wider text-red-500 block mb-1" data-id="element-366">
                    Before
                  </span>
                  {r.before}
                </div>
                <div className="px-4 py-3 bg-green-50 text-sm text-green-900 border-t border-gray-200" data-id="element-367">
                  <span className="text-xs font-semibold uppercase tracking-wider text-green-600 block mb-1" data-id="element-368">
                    After
                  </span>
                  {r.after}
                </div>
              </div>)}
          </div>
        </div>}

      <div className="flex flex-col sm:flex-row gap-3 pt-2" data-id="element-369">
        <button onClick={onReset} className="flex-1 border border-navy-200 text-navy-900 px-6 py-3 rounded-sm font-semibold hover:bg-navy-50 transition-colors flex items-center justify-center" data-id="element-370">
          <RotateCcw className="w-4 h-4 mr-2" data-id="element-371" /> Review Another
        </button>
        <a href="#contact" onClick={onClose} className="flex-1 bg-navy-900 hover:bg-navy-800 text-white px-6 py-3 rounded-sm font-semibold transition-colors flex items-center justify-center text-center" data-id="element-372">
          Book a Consultation
        </a>
      </div>
    </motion.div>;
}